import React, { useMemo } from 'react'

import { toTitleCase, toDollar, centsToDollar, date, capitalize } from 'utils'

import { useSubscriptionPlans, resolveCurrentPlan } from 'hooks/api'
import type { SubscriptionPlan } from 'hooks/api'
import type {
  ActivationUsage,
  ActivationUsageRange,
  UpcomingInvoice,
} from 'services/billingApi'
import {
  InfoIcon,
  ChevronDownIcon,
  CircleCheckBigIcon,
  SparklesIcon,
  BadgeCheckIcon,
} from 'lucide-react'

import {
  useTheme,
  Divider,
  Stack,
  Typography,
  Tooltip,
  AccordionDetails,
  AccordionSummary,
  Accordion,
  Skeleton,
} from '@mui/material'

// Concise help text for the section-header info icons.
const InfoDot = ({ title, color }: { title: string; color: string }) => (
  <Tooltip title={title} arrow placement="top">
    <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
      <InfoIcon strokeWidth={1} color={color} size={18} />
    </span>
  </Tooltip>
)
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Card, Button, EMPTY_VALUE } from '@repo/ui'

import type { ActivationUsagePoint } from 'services/billingApi'
import { INTERNAL_SUBSCRIPTION_STATUSES } from 'constants/statuses'

import { InfoBlock, InfoItem, InfoRow } from '../InfoBlock'
// Direct path, not the `components` barrel — this file is re-exported from it.
import { InfoTooltip } from '../../feedback/InfoTooltip'
import { Progress } from '../Progress'
import { resolveActivationTier, activationCharge } from '@repo/types'
import {
  CORE_FEATURES_ROW,
  planHighlightSections,
  resolvePlanTierKey,
} from 'constants/planComparison'
import { StatusChip } from '../StatusChip'
import { ActivationTriggerSelect } from './ActivationTriggerSelect'
import { ActivationPricingSchedule } from '../ActivationPricingSchedule'
import {
  ActivationRangeSelect,
  activationRangeLabel,
} from './ActivationRangeSelect'

/**
 * Empty-state copy per range. It has to name the window, or a brand looking at
 * twelve empty months reads "No activations yet this period" and concludes the
 * chart is broken rather than that the history is genuinely empty.
 */
const ACTIVATION_RANGE_EMPTY_LABEL: Record<ActivationUsageRange, string> = {
  month: 'No activations yet this period.',
  '6m': 'No activations in the last 6 months.',
  '12m': 'No activations in the last 12 months.',
}

/**
 * Shared Billing Overview surface — the "Next invoice summary" + "Certificate
 * activations" meter + "Plan" cards. Used by both the company-settings Billing
 * page (customer self-service) and the internal company-detail Plan tab, so the
 * two stay in parity (VERA-873).
 *
 * Subscription + plan-change/activation-change actions are injected via props so
 * each context wires its own data/behaviour. The plan catalog (`useSubscriptionPlans`)
 * is a global catalog and is read here directly.
 *
 * NOTE: activation usage + invoice totals are still placeholder/demo values
 * (mirroring the settings page) until the admin billing endpoints land (TD-048).
 */
type BillingSubscription = {
  stripe_subscription_id?: string | null
  status?: string | null
  stripe_product_id?: string | null
  plan?: SubscriptionPlan | null
  current_period_end?: string | null
  canceled_at?: string | null
  trial_end?: string | null
} | null

interface BillingOverviewProps {
  subscription?: BillingSubscription
  /** Full upcoming Stripe invoice (amounts already in dollars from the API). */
  upcomingInvoice?: UpcomingInvoice | null
  upcomingInvoiceDate?: string | Date | null
  /** While true, the Next-invoice-summary cells render skeletons (§19). */
  upcomingInvoiceLoading?: boolean
  /**
   * Real per-company certificate-activation usage (VERA-646). When omitted (e.g.
   * the internal company-detail Plan tab, which has no company-self context) the
   * card falls back to placeholder counts.
   */
  activationUsage?: ActivationUsage | null
  /**
   * While true, the "Activations granted" tile renders a skeleton (§19)
   * instead of "—". `activationUsage` alone can't distinguish "still loading"
   * from "no query context at all" (both are null/undefined — e.g. the
   * internal company-detail Plan tab never fetches usage), so this is a
   * dedicated signal from the caller, mirroring `upcomingInvoiceLoading`.
   */
  activationUsageLoading?: boolean
  /**
   * Lifetime total of admin-granted activation credits (VRG-247 revision).
   * Falls back for the "Activations granted" tile when `activationUsage` is
   * absent — e.g. the internal company-detail Plan tab, which has no
   * activation-usage query context but does have this total from the
   * company-detail response (`grantedActivationsTotal`). The customer-facing
   * Settings → Billing page keeps reading `activationUsage.grantedTotal`
   * unchanged.
   */
  grantedActivationsTotal?: number | null
  /**
   * Window the activations chart is showing. The range control renders only when
   * `onActivationRangeChange` is also supplied, because the range drives a REFETCH
   * (the endpoint buckets server-side) and a card that cannot refetch must not
   * offer a control that appears to do nothing. The internal company-detail Plan
   * tab passes neither.
   */
  activationRange?: ActivationUsageRange
  onActivationRangeChange?: (range: ActivationUsageRange) => void
  /**
   * A usage request is in flight, INCLUDING a background refetch over data that is
   * still on screen. Separate from `activationUsageLoading` on purpose: that one
   * skeletons the granted tile and must not flash on every range switch (§19),
   * while the chart genuinely cannot draw during a switch and needs to know.
   *
   * It is also what keeps the chart's error state honest — "the response does not
   * match the selected range" is only a failure once the request has SETTLED.
   */
  activationUsageFetching?: boolean
  /** Refetch, for the chart's error state. Without it the state has no way out. */
  onRetryActivationUsage?: () => void
  /** Company id — enables the editable activation-trigger control (omitted on the
   *  internal read-only company-detail tab). */
  companyId?: string | null
  onChangePlan?: () => void
  onChangeActivations?: () => void
}

/**
 * Slice B — small activations-over-time chart rendered inside the Certificate
 * activations card. Uses the app's existing charting library (recharts, already
 * a dependency) rather than adding a new dep. Handles three states per
 * working-conventions §19:
 *  - loading (series === null/undefined)  → skeleton at the chart's footprint
 *  - loaded-but-empty (series.length 0)   → "No activations yet" empty state
 *  - loaded-with-data                     → an AreaChart sparkline
 */
const ACTIVATIONS_CHART_HEIGHT = 164

/**
 * Label a `YYYY-MM-DD` bucket key without letting the timezone move it.
 *
 * The key is a UTC CALENDAR bucket, not an instant. `new Date('2026-08-21')`
 * parses it as UTC midnight, and `date()` then formats in LOCAL time, so west of
 * Greenwich every bucket slid a day earlier: the daily chart was labelling
 * Aug 21 and Aug 23 as "Aug 20" and "Aug 22", and the monthly one turned March
 * into February for all twelve buckets. Appending a bare time makes the string
 * parse as local, which preserves the calendar date exactly at any offset — there
 * is no conversion left to get wrong.
 */
const bucketLabel = (bucket: string, format?: string): string | null =>
  date(`${bucket}T00:00:00`, format)

/**
 * The chart's footprint, shared by every state it can be in, so a skeleton, an
 * empty state and an error all occupy exactly the space the chart will.
 */
const chartPlaceholderSx = (background: string) =>
  ({
    height: ACTIVATIONS_CHART_HEIGHT,
    width: '100%',
    borderRadius: 2,
    background,
  }) as const

const ActivationsChart = ({
  series,
  granularity = 'day',
  emptyLabel = 'No activations yet this period.',
  unavailable = false,
  unavailableHint,
  onRetry,
}: {
  series: ActivationUsagePoint[] | null | undefined
  /** Bucket size of `series`, from the API. Decides the x-axis label format. */
  granularity?: 'day' | 'month'
  /** Empty-state copy — the range decides what "nothing here" means. */
  emptyLabel?: string
  /**
   * The response settled and does not describe the selected range, so there is
   * nothing honest to draw. Distinct from loading, and that distinction is the
   * whole point: it used to be indistinguishable, so the card sat on a skeleton
   * forever with no chart, no empty state and nothing to click.
   */
  unavailable?: boolean
  /** Second line of the error state — names the range that failed. */
  unavailableHint?: string
  onRetry?: () => void
}) => {
  const theme = useTheme()

  /**
   * Cannot draw this range. Checked FIRST, before the loading branch, because it
   * is reached with `series` undefined and would otherwise fall into a skeleton
   * that never resolves.
   *
   * This is what a version-skewed deploy looks like from the browser: a new
   * frontend asking for `range=6m` against an API that does not know the
   * parameter and answers with the month view. The staleness guard is right to
   * reject that response (mislabelled data asserts something false), but a
   * rejection with no visible outcome is its own failure — reproduced live, the
   * card rendered a permanent grey block and no reason for it. An error state
   * says what happened and offers the two ways out.
   */
  if (unavailable) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={3}
        sx={chartPlaceholderSx(theme.palette.primary[50])}
      >
        <Typography variant="body3" color="text.primary">
          This chart could not be loaded.
        </Typography>
        {unavailableHint && (
          <Typography
            variant="body4"
            color="text.secondary"
            sx={{ textAlign: 'center', maxWidth: 380, lineHeight: 1.5 }}
          >
            {unavailableHint}
          </Typography>
        )}
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Stack>
    )
  }

  // Loading: no series resolved yet — skeleton occupying the same footprint.
  if (series == null) {
    return (
      <Skeleton
        variant="rounded"
        height={ACTIVATIONS_CHART_HEIGHT}
        sx={{ width: '100%', borderRadius: 2 }}
      />
    )
  }

  /**
   * Empty means "nothing happened", NOT "no rows came back".
   *
   * Monthly ranges arrive zero-filled, so a brand with no history at all returns
   * 6 or 12 points that are every one of them zero. Drawing that as an area chart
   * gives a flat line pinned to the bottom of a hidden axis, which reads as a
   * broken chart rather than as the true statement it is. A sparse-but-nonzero
   * history is a different case and DOES draw: five flat months and a spike is
   * exactly what happened, and hiding it behind an empty state would be the
   * dishonest option.
   */
  if (series.length === 0 || series.every((point) => point.count === 0)) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={3}
        // Match the chart's exact footprint (height + full width) so the empty
        // state occupies the same space the chart will. Subtle greyed fill, no
        // border — reads as "the chart renders here" rather than blank space.
        sx={chartPlaceholderSx(theme.palette.primary[50])}
      >
        {/* Icon-in-circle on the card's base surface so it stands off the tint. */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ p: 4, borderRadius: 50, background: theme.palette.background.paper }}
        >
          <BadgeCheckIcon
            strokeWidth={1}
            size={24}
            color={theme.palette.grey[600]}
          />
        </Stack>
        <Typography variant="body3" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Stack>
    )
  }

  // Daily buckets keep the existing full date. Monthly buckets get the month
  // alone, plus the year once the window can span a year boundary — "Jan" after
  // "Dec" is ambiguous across 12 months and not across 6.
  const monthFormat = series.length > 6 ? 'MMM yy' : 'MMM'
  const isMonthly = granularity === 'month'
  const data = series.map((point) => ({
    ...point,
    label: bucketLabel(point.date, isMonthly ? monthFormat : undefined),
  }))

  const axis = (
    <>
      <XAxis
        dataKey="label"
        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
        tickLine={false}
        axisLine={false}
        // Monthly windows are at most 12 points, and every one of them is a
        // labelled bucket a reader may want to identify, so label them all. Daily
        // keeps the gap-based thinning — 31 full dates will not fit.
        {...(isMonthly ? { interval: 0 } : { minTickGap: 24 })}
      />
      <YAxis hide allowDecimals={false} />
      <RechartsTooltip
        cursor={
          isMonthly
            ? { fill: theme.palette.action.hover }
            : { stroke: theme.palette.grey[300] }
        }
        formatter={(value: number | string) => [value, 'Activations']}
        contentStyle={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 8,
          boxShadow: theme.shadows[2],
        }}
        labelStyle={{ color: theme.palette.text.secondary }}
        itemStyle={{ color: theme.palette.text.primary }}
      />
    </>
  )

  /**
   * Monthly buckets are BARS; daily buckets keep the area sparkline.
   *
   * The mark has to match what the numbers are. A month's activations is a
   * discrete total, and a smoothed area drawn through six of them interpolates
   * values that do not exist — a `0, 0, 0, 1, 0, 2` history rendered as a
   * monotone curve reads as gently rising activity, which is the opposite of
   * what happened. Bars say "three months of nothing, then one, then none, then
   * two", which is the truth and is exactly the sparse case this range has to
   * survive. Daily data over one month is a genuine trend line and keeps the
   * area, which is also what the default view has always looked like.
   */
  return (
    <Stack sx={{ height: ACTIVATIONS_CHART_HEIGHT, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        {isMonthly ? (
          <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            {axis}
            <Bar
              dataKey="count"
              fill={theme.palette.brand[500]}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
              isAnimationActive={false}
            />
          </BarChart>
        ) : (
          <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="activations-area" x1="0" y1="0" x2="0" y2="1">
                {/* brand[500], not primary.main. The primary ramp is an INK token —
                    literally #000000 in light mode and #ffffff in dark — so the series was
                    painted with the same value as body text and read as chrome rather than
                    data. Validated: #000000 FAILS both the lightness-band and chroma-floor
                    checks on a light surface ("below floor — reads gray"); #8F68EA passes
                    all four checks against BOTH the light and dark card surfaces. A fixed
                    hue across modes is correct here: it is the series' identity, not a
                    surface colour, and it matches the activation meter directly below,
                    which already uses brand tints for the same metric. Applies equally to
                    the monthly bars above — one series, one colour, however it's drawn. */}
                <stop
                  offset="0%"
                  stopColor={theme.palette.brand[500]}
                  stopOpacity={0.3}
                />
                <stop
                  offset="100%"
                  stopColor={theme.palette.brand[500]}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            {axis}
            <Area
              type="monotone"
              dataKey="count"
              stroke={theme.palette.brand[500]}
              strokeWidth={2}
              fill="url(#activations-area)"
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </Stack>
  )
}

/**
 * One canonical plan row in the "More details" list — label left, value right.
 *
 * NO leading check icon, deliberately. In the matrix a check is INFORMATION: it
 * separates included from not-included across four columns, and a `false` cell
 * renders an em dash beside it. Here there is one tier and every `false` row has
 * already been filtered out, so a check on every row distinguishes nothing — it
 * was 17 identical glyphs of noise around the words that carry the meaning.
 * Presence in this list IS the "included" signal.
 *
 * So the row reads as a definition list: the label on the left, and for rows
 * whose value is a measurement rather than a yes/no ("Team seats -> 5",
 * "Scan history -> Full") the value right-aligned, in the same `body3` the matrix
 * uses for its non-boolean cells. Rows whose value is just `true` show the label
 * alone, which is the whole statement.
 */
const PlanDetailRow = ({
  label,
  term,
  tooltip,
  value,
  emphasized = false,
}: {
  label: string
  /** Glossary entry id — preferred over `tooltip` for a term of art (§44). */
  term?: string
  tooltip?: string
  value: string | boolean
  emphasized?: boolean
}) => {
  const theme = useTheme()
  const hasTooltip = Boolean(term) || Boolean(tooltip)
  // Label + dot are ONE hover target — see the note on `InfoItem`. A 16px dot is
  // a target nobody goes looking for; the words beside it are what gets read.
  const labelGroup = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ width: 'fit-content', ...(hasTooltip ? { cursor: 'help' } : null) }}
    >
      <Typography
        variant="body3"
        color="text.primary"
        fontWeight={emphasized ? 500 : 400}
      >
        {label}
      </Typography>
      {hasTooltip && <InfoIcon strokeWidth={1} color={theme.palette.grey[500]} size={18} />}
    </Stack>
  )
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      {hasTooltip ? (
        <InfoTooltip term={term} text={tooltip}>
          {labelGroup}
        </InfoTooltip>
      ) : (
        labelGroup
      )}
      {typeof value === 'string' && (
        <Typography variant="body3" color="text.secondary" sx={{ ml: 'auto' }}>
          {value}
        </Typography>
      )}
    </Stack>
  )
}

/**
 * The four activation stats share one baseline for their VALUES.
 *
 * `InfoItem` defaults to `alignSelf: 'baseline'`, which aligns the four LABELS —
 * fine when every label is one line, and these are not: they run one to three
 * lines each, so the values landed at four different heights and the strip read
 * as four unrelated boxes rather than one row of figures. Stretching each cell to
 * the row's height and pushing the value to the bottom puts the numbers on a
 * shared line, which is the line the eye actually scans across.
 */
const STAT_SX = { alignSelf: 'stretch', justifyContent: 'space-between' } as const

/** `max_certificates` -> `Max certificates`. */
const humanizeFeatureKey = (key: string): string => {
  const words = key.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Plan features arrive in two shapes and BOTH used to render garbage.
 *
 * Array — what `sync-stripe-catalog.ts` writes, from Stripe `marketing_features`
 * mapped to `f.name`. Already strings. But an entry that is still an object (a raw
 * `{name}`) stringified to `[object Object]`, so unwrap `name`/`label` first.
 *
 * Object — what `database/seed.ts` writes (`{max_certificates: 1000, api_access:
 * true}`). This branch used to be `Object.values(...)`, which DISCARDED THE KEYS and
 * rendered the bullets "1000" and "true" — all value, no meaning. (They appeared in
 * the other order because jsonb sorts keys by length, putting `api_access` first.)
 * Keys are the half that carries the meaning, so pair them back up:
 *   - `true`  -> the label alone ("API access"); the flag being on IS the feature
 *   - `false` -> dropped entirely; a feature you do NOT get is not a selling point
 *   - other   -> "Label: value", numbers localized so 1000 reads as 1,000
 */
const extractPlanFeatures = (
  features: SubscriptionPlan['features'] | undefined,
): string[] => {
  if (!features) return []

  if (Array.isArray(features)) {
    return features
      .map((f) => {
        if (typeof f === 'string') return f
        if (f && typeof f === 'object') {
          const named = f as { name?: unknown; label?: unknown }
          if (typeof named.name === 'string') return named.name
          if (typeof named.label === 'string') return named.label
        }
        return String(f)
      })
      .filter(Boolean)
  }

  if (typeof features === 'object') {
    return Object.entries(features as Record<string, unknown>)
      .filter(([, value]) => value !== false && value != null)
      .map(([key, value]) => {
        const label = humanizeFeatureKey(key)
        if (value === true) return label
        if (typeof value === 'number') return `${label}: ${value.toLocaleString()}`
        return `${label}: ${String(value)}`
      })
  }

  return []
}

export const BillingOverview = ({
  subscription,
  upcomingInvoice = null,
  upcomingInvoiceDate = null,
  upcomingInvoiceLoading = false,
  activationUsage = null,
  activationUsageLoading = false,
  grantedActivationsTotal = null,
  activationRange = 'month',
  onActivationRangeChange,
  activationUsageFetching = false,
  onRetryActivationUsage,
  companyId = null,
  onChangePlan,
  onChangeActivations,
}: BillingOverviewProps) => {
  const theme = useTheme()
  const { data: plans = [] } = useSubscriptionPlans()

  // Resolve the company's actual current plan from its subscription (VERA-648):
  // prefer the embedded plan, else match the subscription's Stripe product id
  // against the global plan catalog. Falls back to null (renders a skeleton) when
  // the caller has no product context (e.g. the internal company-detail Plan tab).
  // The resolution itself now lives in `resolveCurrentPlan` — the sidebar plan label
  // is the second caller, and this fallback is exactly the kind of thing that drifts
  // when copied (working-conventions §36).
  const currentPlan = useMemo<SubscriptionPlan | null>(
    () => resolveCurrentPlan(subscription, plans),
    [subscription, plans],
  )

  /**
   * "More details" renders the CANONICAL comparison rows for this tier — the same
   * rows, from the same source, that the plan-comparison matrix shows in its
   * column for this plan (`constants/planComparison`, working-conventions §36).
   * One column here, not the matrix's four.
   *
   * The plan's own `features` is the FALLBACK, not the primary: it is free-text
   * Stripe `marketing_features` with "Plus everything in <tier>" inheritance, so
   * it says different things than the matrix for the same plan. It still covers
   * the case the canonical rows can't describe — a tier the row set doesn't know
   * (a hand-made Stripe product, or a plan predating `tier_key`) — so an
   * unrecognised plan shows its own copy rather than an empty panel.
   */
  const planTierKey = useMemo(
    () => resolvePlanTierKey(currentPlan),
    [currentPlan],
  )
  const planHighlights = useMemo(
    () =>
      planHighlightSections(
        planTierKey,
        currentPlan?.included_activations ?? null,
        // VRG-438. `undefined` when there is no plan at all, which is how
        // planHighlightSections tells "no seat data" from `null` (= unlimited) —
        // the row is omitted rather than claiming an allowance nothing resolved.
        currentPlan ? (currentPlan.included_seats ?? null) : undefined,
      ),
    [planTierKey, currentPlan],
  )
  const planFeatures = useMemo(
    () => (currentPlan ? extractPlanFeatures(currentPlan.features) : []),
    [currentPlan],
  )

  // Plan price + billing interval — catalog amounts are stored in cents (Stripe
  // convention), so go through the shared `centsToDollar`, same as the
  // plan-change / checkout / signup surfaces. Decimals stay ON here (`$29.00`),
  // which is a real divergence from those pages' `$29` — preserved deliberately
  // rather than silently unified as a side effect of the de-duplication.
  const planPriceLabel = useMemo(() => {
    if (currentPlan?.amount == null) return null
    const interval =
      currentPlan.interval === 'year'
        ? 'year'
        : currentPlan.interval === 'month'
          ? 'month'
          : currentPlan.interval
    return `${centsToDollar(currentPlan.amount, false)}${interval ? ` / ${interval}` : ''}`
  }, [currentPlan])

  // Renewal / cancellation line. Prefer the upcoming-invoice period end (same value
  // the "Next invoice date" uses) so the two stay consistent; fall back to the
  // subscription's own current_period_end.
  const renewalLabel = useMemo(() => {
    const periodEnd =
      upcomingInvoice?.dueDate ??
      upcomingInvoice?.periodEnd ??
      subscription?.current_period_end
    if (!periodEnd) return null
    const when = date(periodEnd, 'MMM d, yyyy')
    if (!when) return null
    return subscription?.canceled_at ? `Cancels ${when}` : `Renews ${when}`
  }, [
    upcomingInvoice?.dueDate,
    upcomingInvoice?.periodEnd,
    subscription?.current_period_end,
    subscription?.canceled_at,
  ])

  // VERA-646 / TD-065 — real per-company activation counts come from
  // `activationUsage` (the certificate-metering pipeline). The graduated tier
  // schedule + the plan's included allotment are the real pricing model
  // (gtm/pricing.md §3), sourced from the plan's own `activation_tiers`
  // (VRG-375 — read off the API, not a hardcoded constant) +
  // `currentPlan.included_activations`.
  const activationCount = activationUsage?.currentCount ?? 0
  const previousActivations = activationUsage?.previousCount ?? 0

  /**
   * The comparison against last month, as ONE self-contained phrase or as nothing
   * at all.
   *
   * It used to be three sibling fragments — the count, an optional percentage, and
   * the bare words "Last month" — and the optional one was the problem. It
   * returned null whenever `previousCount` was falsy, so the two that remained
   * closed up into "2 Last month", which asserts that last month's figure was 2.
   * It was not; 2 is THIS period. A fragment that can vanish and leave a
   * grammatical, false sentence behind is not a fragment, it is a bug.
   *
   * So the number now carries its own label ("This billing period") and the
   * comparison is a single element that either renders whole or not at all.
   *
   * The three states it distinguishes, which the old `if (!previousActivations)`
   * collapsed into two:
   *
   *   1. NO PRIOR PERIOD (the company did not exist last month). There is nothing
   *      to compare, so nothing renders. `previousPeriodExists` comes from the
   *      company's age, not from its activation count, because "was there a last
   *      month" is a question about the company.
   *   2. PRIOR PERIOD, ZERO ACTIVATIONS. A real, reportable change that the old
   *      falsy check hid. A percentage against a zero base is undefined (not
   *      infinite, not 100%), so the honest figure is the ABSOLUTE delta.
   *   3. PRIOR PERIOD, NONZERO. A percentage, which is what a reader wants once
   *      there is a base to take one against.
   */
  const activationComparison = useMemo<{
    label: string
    tone: 'up' | 'down' | 'flat'
  } | null>(() => {
    if (!activationUsage?.previousPeriodExists) return null

    const delta = activationCount - previousActivations
    if (delta === 0) return { label: 'No change vs last month', tone: 'flat' }

    const tone = delta > 0 ? 'up' : 'down'
    const sign = delta > 0 ? '+' : '-'

    if (previousActivations === 0) {
      return {
        label: `${sign}${Math.abs(delta).toLocaleString()} vs last month`,
        tone,
      }
    }

    // Round to a whole percent — two decimals on a ratio of small integers is
    // false precision. Sub-1% changes keep one decimal so they do not round away
    // to "+0%", which would read as no change when there was one.
    const magnitude = Math.abs((delta / previousActivations) * 100)
    const formatted =
      magnitude < 1
        ? magnitude.toFixed(1)
        : Math.round(magnitude).toLocaleString()
    return { label: `${sign}${formatted}% vs last month`, tone }
  }, [activationUsage?.previousPeriodExists, activationCount, previousActivations])

  const activationComparisonColor =
    activationComparison?.tone === 'up'
      ? 'success.main'
      : activationComparison?.tone === 'down'
        ? 'error.main'
        : 'text.secondary'

  /**
   * The response the chart is allowed to draw — the whole object, so the series
   * and the label format it is rendered with can never come from different
   * responses.
   *
   * It has to be gated because `placeholderData` deliberately keeps the PREVIOUS
   * range's response on screen while the next loads (the headline count, the
   * delta and the tier meter are range-independent and must not blank out).
   * Drawing that series under a control claiming a different range would put the
   * wrong months behind the right label, which is the same class of quiet false
   * statement as the "2 Last month" this task exists to fix. `null` puts the
   * chart on its skeleton instead (§19).
   *
   * The version-skew case is narrow ON PURPOSE. An API predating `range` can only
   * be returning the legacy month view, so its response is accepted for `month`
   * and for nothing else. An earlier version of this accepted a range-less
   * response for ANY selection, and driving it proved the cost immediately: the
   * daily August series rendered as two bars both labelled "Aug" underneath a
   * "6 months" control. A permanent skeleton says "cannot show this"; mislabelled
   * data asserts something false, which is strictly worse.
   */
  const displayedActivationUsage =
    activationUsage &&
    (activationUsage.range
      ? activationUsage.range === activationRange
      : activationRange === 'month')
      ? activationUsage
      : null

  /**
   * The rejection above, made VISIBLE.
   *
   * Rejecting a mismatched response is correct; rendering nothing as a result is
   * not. `series` came through as `undefined` and landed on the loading branch, so
   * a permanent skeleton was the entire user-facing outcome of a real failure —
   * reproduced live against an API that predates `range`, which is exactly what a
   * half-deployed frontend looks like.
   *
   * The `activationUsageFetching` term is what separates "failed" from "still
   * going": during a range switch the previous response is deliberately kept on
   * screen (`placeholderData`) and mismatches by design, and that is a skeleton,
   * not an error. Only a mismatch that survives the request settling is a failure.
   */
  const activationChartUnavailable =
    Boolean(activationUsage) &&
    displayedActivationUsage == null &&
    !activationUsageFetching &&
    !activationUsageLoading

  /**
   * Name the window that failed, and offer the one that is known to work — except
   * when the one that failed IS the default, where "switch back to This month"
   * would be advice to stay where you are.
   */
  const activationChartUnavailableHint =
    activationRange === 'month'
      ? 'Activations for this billing period did not load. Try again in a moment.'
      : `The "${activationRangeLabel(activationRange)}" view did not load. Try again, or switch the chart back to This month.`

  // The current plan's own graduated schedule. `null` when the sync hasn't
  // resolved tiers out of Stripe (or there's no current plan yet) — everything
  // derived from it below must hide rather than render a wrong/blank number
  // (VRG-375 AC4).
  const activationTiers = currentPlan?.activation_tiers ?? null

  // Where this month's count sits in the graduated schedule (current band + rate,
  // next band's rate, remaining to the next cheaper band). `null` when the
  // schedule is unusable.
  const activationTier = resolveActivationTier(activationCount, activationTiers)
  const includedActivations = currentPlan?.included_activations ?? 0
  const planActivationValue = includedActivations
    ? `${Math.min(activationCount, includedActivations)}/${includedActivations}`
    : null
  const activationsToNextTier = activationTier?.toNextTier ?? null

  // Milestone circles for the meter: Free (the plan's included allotment) → the
  // current per-activation rate (starts once the free allotment is used) → the
  // next tier's rate. Each fills in as the count passes it. Only applies while
  // the free allotment sits inside the current band (i.e. the first tier), the
  // current band is bounded, and there IS a current band; past that we fall
  // back to the plain band circles.
  const useActivationStops =
    activationTier != null &&
    includedActivations > 0 &&
    activationTier.upperBound !== null &&
    includedActivations >= activationTier.lowerBound &&
    includedActivations <= activationTier.upperBound
  const activationStops =
    useActivationStops && activationTier && activationTier.upperBound !== null
      ? [
          {
            position: activationTier.lowerBound,
            label: 'Free',
            active: true,
          },
          {
            position: includedActivations,
            label: `$${activationTier.currentRate}`,
            subLabel: includedActivations.toLocaleString(),
            active: activationCount >= includedActivations,
          },
          {
            position: activationTier.upperBound,
            label: `$${activationTier.nextRate ?? activationTier.currentRate}`,
            subLabel: activationTier.upperBound.toLocaleString(),
            active: activationCount >= activationTier.upperBound,
          },
        ]
      : undefined
  /**
   * What the plan covered, as a SAVING — not as a cost.
   *
   * This tile used to read "Cost at list price": `activations x list rate`, the
   * allotment ignored. At 2 activations against 12 included that renders $50.00,
   * while the customer's actual charge is $0.00 — and the "Certificate
   * activations" line in the Next invoice summary, directly above on the same
   * screen, shows that $0.00. A tile reading $50 a few inches under an invoice
   * line reading $0 invites exactly one inference, and it is the wrong one.
   *
   * The number is not wrong, its FRAME was: $50 is the list value of activations
   * the plan absorbed, which is money the customer did not pay. So the figure
   * stays and the label says what it is. Naming THIS tile "Total activation cost"
   * is specifically the reading being fixed here and must not come back.
   *
   * (The Next invoice summary above does carry a tile by that name, and it is a
   * different quantity: the OVERAGE the plan did not cover, which is a real cost.
   * Covered-here + cost-there = the full ladder, by construction. The label is
   * only wrong on a figure the customer was never charged.)
   *
   * WHEN THE ALLOTMENT IS EXCEEDED it stops climbing — which is still exactly
   * true (the plan covered those and no more), and the cost it does NOT cover is
   * stated as money on the invoice summary above. Keeping the two from being two
   * different answers to "what do these activations cost" is the whole point.
   *
   * The saving is the allotment credited at the ENTRY rate, capped at what was
   * actually used — `gtm/pricing.md` §4. It comes out of the same
   * `activationCharge` call as the cost, which is what guarantees covered + cost
   * = ladder(total) at every count rather than by coincidence.
   */
  /**
   * COST and SAVING both come from `activationCharge` — one function, one canon
   * citation (`gtm/pricing.md` §6), so the two tiles cannot drift apart. The
   * formula and the two rejected alternatives are documented there, not here;
   * this file having its own copy is how it got the arithmetic wrong twice in
   * one afternoon.
   *
   * KNOWN IMPRECISION, affecting only the no-invoice fallback: the real covered
   * count is the LEDGER balance, which carries rollover from unused periods and
   * admin grants (`company_activation_ledger`, VRG-247) — not just this plan's
   * monthly allotment. A company on banked credit is covered for more than this
   * estimates. Once an invoice exists Stripe's figure wins and the point is moot.
   *
   * Takes `activationTiers` as its third argument (VRG-375 — the schedule now
   * comes off the plan, not a hardcoded constant) and returns `null` rather than
   * `{cost, covered}` when that schedule is unusable, so the no-invoice fallback
   * below has to handle a null `cost` the same way every other tier-derived value
   * on this card does (AC4).
   */
  const activationChargeResult = activationCharge(
    activationCount,
    includedActivations,
    activationTiers,
  )
  const activationCostValue = activationChargeResult?.cost ?? null

  // Next invoice summary (VERA-650) — real Stripe upcoming-invoice data. The API
  // returns amounts already in dollars (centsToDollars), so format directly.
  const invoiceLineItems = upcomingInvoice?.lineItems ?? []
  const planLineItem = invoiceLineItems.find(
    (li) =>
      (currentPlan?.stripe_product_id != null &&
        li.productId === currentPlan.stripe_product_id) ||
      (currentPlan?.stripe_price_id != null &&
        li.priceId === currentPlan.stripe_price_id),
  )
  // Anything that isn't the recurring plan line is usage/metering (e.g. the
  // certificate-activation metered charge) for this invoice period.
  const nonPlanLineTotal = invoiceLineItems
    .filter((li) => li !== planLineItem)
    .reduce((sum, li) => sum + (li.amount ?? 0), 0)
  const planChargeLabel = planLineItem
    ? toDollar(planLineItem.amount ?? 0, false)
    : EMPTY_VALUE
  const nextInvoiceActivationsCharge = invoiceLineItems.length
    ? toDollar(nonPlanLineTotal, false)
    : EMPTY_VALUE

  /**
   * Stripe first, computed fallback — and NEVER blank, UNLESS the schedule
   * itself is unusable. Stripe is authoritative once an upcoming invoice
   * exists, but before the first metered line is reported there is no invoice
   * to read, and a dash there reads as "unknown" when the answer is a
   * confident $0.00. The computed ladder models the same quantity Stripe will
   * bill, so falling back to it changes precision, not meaning — except when
   * `activationCostValue` is null (VRG-375 AC4: no schedule resolved out of
   * Stripe), where there is no honest number to fall back to and '—' is the
   * correct answer, same as every other tier-derived tile on this card.
   */
  const totalActivationCost = invoiceLineItems.length
    ? toDollar(nonPlanLineTotal, false)
    : activationCostValue != null
      ? toDollar(activationCostValue, false)
      : EMPTY_VALUE

  const upcomingInvoiceTotal = upcomingInvoice?.total ?? null
  const resolvedInvoiceDate =
    upcomingInvoiceDate ??
    upcomingInvoice?.dueDate ??
    upcomingInvoice?.periodEnd ??
    null
  // `date()` returns null on a missing date. No `?? '—'` here any more: `InfoItem`
  // now renders the dash for a null value itself, so the fallback that used to
  // live at this call site — and that was MISSING here, which is how this one tile
  // shipped blank while its three neighbours showed a dash — is redundant. One
  // source, and a call site can no longer forget it.
  const nextInvoiceDateLabel = date(resolvedInvoiceDate)

  return (
    <Stack>
      {/* Flexbox `gap` (not Stack `spacing`) so the CSS-`order` reordering of the
          cards keeps a uniform visual gap — margin-based spacing follows DOM order
          and left the cards unevenly spaced once reordered. 24px sits between the
          two previous gaps. */}
      <Stack sx={{ gap: 6, pt: 2 }}>
        <InfoBlock>
          <Typography variant="body1" p={6} pb={2}>
            Next invoice summary
          </Typography>
          <InfoRow>
            <InfoItem
              label="Plan"
              value={
                upcomingInvoiceLoading ? undefined : (
                  <Typography variant="body2" color="text.primary">
                    {planChargeLabel}
                  </Typography>
                )
              }
            />
            {/* "Activations", not "Certificate activations": it sits beside
                "Plan" as the other half of the invoice, and the two line items
                read as a pair only if they are the same shape. The card below
                uses the same word for the same thing. */}
            <InfoItem
              label="Activations"
              value={
                upcomingInvoiceLoading ? undefined : (
                  <Typography variant="body2" color="text.primary">
                    {nextInvoiceActivationsCharge}
                  </Typography>
                )
              }
            />
            {/* "Total activation cost" belongs to the Activations card below, at
                position 3 of its stat strip — not here. It briefly lived in this
                row and was a near-duplicate of the "Certificate activations" line
                immediately to its left: same money, differing only in what each
                falls back to with no invoice. Two tiles for one number. */}
          </InfoRow>
          <Divider sx={{ mr: 4, ml: 4 }} />
          <InfoRow>
            {/* The one figure in this block that is not self-describing: it is a
                DRAFT that moves as the period runs (plan fee + activations billed
                so far), not a settled amount. The glossary entry says exactly that,
                and says it in one place (§44) — the same sentence would otherwise
                get retyped here and on any other surface that shows it. */}
            <InfoItem
              term="next-invoice-total"
              label="Next invoice total"
              value={
                upcomingInvoiceLoading ? undefined : (
                  <Typography variant="h1" sx={{ fontSize: 32 }}>
                    {upcomingInvoiceTotal != null
                      ? toDollar(upcomingInvoiceTotal, false)
                      : EMPTY_VALUE}
                  </Typography>
                )
              }
            />
            <InfoItem
              label="Next invoice date"
              value={
                upcomingInvoiceLoading ? undefined : nextInvoiceDateLabel
              }
            />
          </InfoRow>
        </InfoBlock>

        <Stack sx={{ order: 2 }}>
          <Card>
            <Stack spacing={6}>
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={4} alignItems="center">
                  {/* Both this header and the next-invoice line item are
                      "Activations" — the line item was "Certificate activations"
                      until it was shortened to sit parallel with "Plan" beside it. */}
                  <Typography variant="body1" color="text.primary">
                    Activations
                  </Typography>
                  {/* Reads the `activation` definition rather than retyping one.
                      The string this replaced — "…included with your plan are
                      free; beyond that, each is billed at the graduated
                      per-activation rate" — was a near-verbatim second copy of
                      the `included-activations` entry, which is rendered by its
                      own tile a few hundred pixels down this same card. Two
                      copies of one sentence on one card is precisely the drift
                      §44 exists to prevent.

                      `activation` and not `included-activations`: this header
                      labels a COUNT of activations, so what it owes the reader is
                      what one IS and that it is the billed unit. The pricing half
                      is carried by the two things that own it — the
                      `included-activations` tile below, and the graduated-rate
                      tooltip on the schedule summary line.

                      Deliberately the DEFAULT glyph, not the 18px InfoIcon the
                      two sibling card headers still use via `InfoDot`: a custom
                      child is not cloned to carry the overlay's focus-restore ref
                      (see InfoTooltip's docstring), and this entry HAS a `long`
                      body, so the overlay path is live. Those two headers explain
                      a SECTION rather than define a term, so they keep literal
                      text; migrating them to `<InfoTooltip text=…>` would unify
                      the glyph without retyping anything. */}
                  <InfoTooltip term="activation" />
                </Stack>
                {/* The card's two pickers, as one group (§29 — 12px).
                    Chart range LEFT, activation trigger RIGHT: the trigger is the
                    consequential one (it decides when a certificate activates and
                    is therefore billed), so it keeps the outer, more-reachable
                    position, and the chart control reads as the lesser of the two
                    by sitting inboard of it. */}
                <Stack direction="row" spacing={3} alignItems="center">
                  {onActivationRangeChange && (
                    <ActivationRangeSelect
                      value={activationRange}
                      onChange={onActivationRangeChange}
                      // Eligibility is the company's AGE, from the same `created_at`
                      // the prior-period comparison reads. Undefined (older API)
                      // leaves every range enabled.
                      availableRanges={activationUsage?.availableRanges}
                      companyCreatedAt={activationUsage?.companyCreatedAt}
                      companyName={activationUsage?.companyName}
                    />
                  )}
                  {/* Shows + edits the company's activation trigger (the setting
                      that decides when a cert activates + is metered), reading and
                      writing the same certificate_trigger_settings field the
                      Features page uses. */}
                  <ActivationTriggerSelect companyId={companyId} />
                </Stack>
              </Stack>

              <Stack direction="row" alignItems="flex-end" spacing={2}>
                <Typography variant="h1" sx={{ fontSize: 32 }}>
                  {activationCount.toLocaleString()}
                </Typography>
                {/* The number is labelled with the period it belongs to, so it
                    cannot be read as belonging to the comparison beside it. */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ pb: 2.5 }}
                >
                  <Typography variant="body3" color="text.secondary">
                    This billing period
                  </Typography>
                  {activationComparison && (
                    <>
                      <Typography variant="body3" color="text.secondary">
                        ·
                      </Typography>
                      <Typography
                        variant="body3"
                        color={activationComparisonColor}
                      >
                        {activationComparison.label}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>

              {/* Slice B (VERA-646) — activations over time. The range control
                  used to sit here, under the chart, on the reasoning that it moves
                  the chart alone. It now sits in the card's top band instead: as a
                  segmented control its filled selected state was the loudest thing
                  on the card, and the scoping it was placed here to convey is
                  carried by its "Chart:" label, which says it in words rather than
                  by proximity. */}
              <ActivationsChart
                series={displayedActivationUsage?.series}
                // Read off the SAME response as the series, never derived from
                // the selected range — that is what keeps the mark and the axis
                // labels describing the data actually on screen.
                granularity={displayedActivationUsage?.granularity ?? 'day'}
                emptyLabel={ACTIVATION_RANGE_EMPTY_LABEL[activationRange]}
                unavailable={activationChartUnavailable}
                unavailableHint={activationChartUnavailableHint}
                onRetry={onRetryActivationUsage}
              />

              {/* Position within the current graduated band: from the band's
                  lower bound to the next (cheaper) boundary, with the current
                  per-activation rate and the next band's rate on the nodes.
                  pt here (not just the meter's own mt) reliably clears the
                  above-circle threshold labels from the chart card above.
                  `activationTier` is null when the schedule is unusable
                  (VRG-375 AC4) — hide the meter entirely rather than render a
                  0/0 bar. */}
              {activationTier && (
                <Stack sx={{ pt: 6 }}>
                  <Progress
                    showCircles
                    bottomLabels={[
                      activationTier.lowerBound.toLocaleString(),
                      activationTier.upperBound !== null
                        ? activationTier.upperBound.toLocaleString()
                        // Unbounded final band: `lowerBound` here IS the
                        // second-to-last tier's `upTo` (resolveActivationTier
                        // sets it from the previous tier), so it's the correct
                        // "starts here" boundary for the "X+" label with no
                        // need to reach back into the tiers array.
                        : `${activationTier.lowerBound.toLocaleString()}+`,
                    ]}
                    value={
                      activationTier.upperBound !== null &&
                      activationTier.upperBound > activationTier.lowerBound
                        ? ((activationCount - activationTier.lowerBound) /
                            (activationTier.upperBound - activationTier.lowerBound)) *
                          100
                        : 100
                    }
                    min={activationTier.lowerBound}
                    max={
                      activationTier.upperBound !== null
                        ? activationTier.upperBound
                        : activationTier.lowerBound
                    }
                    minCircle={`$${activationTier.currentRate}`}
                    maxCircle={`$${activationTier.nextRate ?? activationTier.currentRate}`}
                    // Free → $rate → next-$rate milestone circles (fall back to the
                    // plain band circles once the free allotment is used up).
                    stops={activationStops}
                    // "You are here." The ladder alone renders as a price list; this
                    // is the only thing on it that says where this company stands.
                    // The label is the bare FIGURE: the card already prints "N /
                    // This billing period" at 32px directly above, so a caption here
                    // would be the third time the same number is spelled out. What
                    // the marker adds is where that number falls on the ladder.
                    tick={activationCount}
                    tickLabel={activationCount.toLocaleString()}
                  />
                </Stack>
              )}

              {/* "Where does this end" — the meter above only shows the current
                  and next band; this gives the full graduated schedule the
                  context it lacks alone (§ shared component reused verbatim on
                  the compare-plans page, so the two surfaces can't drift).
                  Renders nothing itself when `activationTiers` is null (AC4). */}
              <ActivationPricingSchedule
                variant="summary"
                tiers={activationTiers}
                currentCount={activationCount}
              />

              {/*
                Order is the order the reader asks the questions in: what I have
                used against what I get, then what that was WORTH, then what extra
                I hold, then how far to the next discount.

                The money tile is second, immediately after the allotment it is
                derived from, because "2 of 12 used" and "so the plan covered $50"
                are one thought. It used to be last, positioned there when it was
                framed as a cost and the least-wanted figure did not deserve the
                most-read slot; reframed as a saving it is the payoff of the tile
                beside it and belongs next to it.

                Every label reads its explanation from the glossary by `term`
                rather than carrying a retyped sentence (§44). "Plan included
                activations" here and "Included activations / mo" in the Plan
                card's More details are the SAME quantity, so they resolve to one
                entry and cannot drift into two different answers.
              */}
              <InfoBlock>
                <InfoRow>
                  {/* LEFTMOST, deliberately. This is the only place on the page
                      that states what the period's activations cost: the Next
                      invoice summary's "Certificate activations" and "Next invoice
                      total" both read '—' until Stripe has an upcoming invoice. A
                      figure that is the sole answer to "what am I paying" does not
                      belong third in a row of counts, and being the only $ value
                      here it separates better anchoring an end than sitting
                      interior.

                      The cost of this choice, accepted knowingly: the row otherwise
                      reads as a derivation (free allotment -> granted credits ->
                      therefore cost -> when it gets cheaper), and leading with the
                      cost puts the answer before its inputs. `b8cc60921` put the
                      money tile LAST for that reason. A stat strip is scanned, not
                      read as a sentence, so the scan-order argument wins.

                      The label: `a6445377d` renamed this "Covered by your plan"
                      ONLY because the figure was wrong — it read `activations x
                      list rate` with the allotment ignored, showing $50 against an
                      actual charge of $0. Reframing a wrong number as a saving was
                      a workaround for the arithmetic, not a decision about the
                      label. The figure is now the canon charge (`activationCharge`,
                      gtm/pricing.md §6), so the tile says what it is.

                      Stripe-first: its metered line is what the customer is
                      actually billed. NOTE that until
                      VRG-392-Billing.Activation.Charge.Diverges.From.Canon lands,
                      Stripe's figure is the OVERCHARGED one, so this tile can show
                      more than the computed fallback would. That is not a bug in
                      this tile — showing the real charge is the honest reading, and
                      the discrepancy is what VRG-392 exists to fix. */}
                  <InfoItem
                    sx={STAT_SX}
                    term="activation-total-cost"
                    label="Total activation cost"
                    value={totalActivationCost}
                  />
                  <InfoItem
                    sx={STAT_SX}
                    term="included-activations"
                    label="Plan included activations"
                    value={planActivationValue}
                  />
                  {/* Sentence case, like every sibling label. It was Title Case
                      alone in the row. */}
                  <InfoItem
                    sx={STAT_SX}
                    term="granted-activations"
                    label="Granted / earned activations"
                    value={
                      // `undefined` still means LOADING (InfoItem draws the
                      // skeleton); `null` means empty and InfoItem draws the dash,
                      // so neither branch spells the character out any more.
                      //
                      // The second branch was `grantedActivationsTotal ? … : '—'`,
                      // a falsy check that rendered a granted total of ZERO as a
                      // dash — reading "we have no figure" when the figure is
                      // nought. `?? null` keeps 0 as 0 and dashes only a genuinely
                      // absent total.
                      activationUsageLoading
                        ? undefined
                        : activationUsage
                          ? (activationUsage.grantedTotal ?? null)
                          : (grantedActivationsTotal ?? null)
                    }
                  />
                  {/* "…to next tier" was read as the next PLAN. It is the next
                      step DOWN the per-activation rate ladder, and "tier" is the
                      word the pricing model itself uses for those steps. */}
                  <InfoItem
                    sx={STAT_SX}
                    term="activation-price-tier"
                    label="Activations to next price tier"
                    value={activationsToNextTier}
                  />
                </InfoRow>
              </InfoBlock>

            </Stack>
          </Card>
        </Stack>

        <Stack sx={{ order: 1 }}>
          <Card>
            <Stack spacing={4}>
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={4} alignItems="center">
                  <Typography variant="body1" color="text.primary">
                    Plan
                  </Typography>
                  {/*
                    Was an `InfoDot` carrying a hand-typed sentence. It described the CARD
                    ("your plan, price and renewal date; use Change plan to…"), which is
                    what the card already shows — so it explained the layout rather than
                    the word, and left the actual question a brand has here unanswered:
                    what a plan governs, and why the invoice is not simply its price.
                  */}
                  <InfoTooltip term="plan" />
                </Stack>
                {onChangePlan && (
                  // Lean toward upgrade: when the company isn't already on the top
                  // tier, label the CTA "Upgrade plan" and make it primary to draw
                  // attention (it goes to the same change-plan page either way).
                  // On the top tier there's nothing to upgrade to → neutral "Change
                  // plan". Net-new (no plan) also gets the primary emphasis.
                  <Button
                    variant={
                      !currentPlan || currentPlan.title !== 'Enterprise'
                        ? 'primary'
                        : 'secondary'
                    }
                    onClick={onChangePlan}
                  >
                    {currentPlan && currentPlan.title !== 'Enterprise'
                      ? 'Upgrade plan'
                      : 'Change plan'}
                  </Button>
                )}
              </Stack>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={3}>
                  {currentPlan ? (
                    <Typography variant="body1" sx={{ fontSize: 20, fontWeight: 600 }}>
                      {currentPlan.title ?? EMPTY_VALUE}
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={160} height={28} />
                  )}
                  {subscription?.status && (
                    <StatusChip
                      size="small"
                      status={subscription.status}
                      statuses={INTERNAL_SUBSCRIPTION_STATUSES}
                    />
                  )}
                </Stack>
                {currentPlan ? (
                  (planPriceLabel || renewalLabel) && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      {planPriceLabel && (
                        <Typography variant="body2" color="text.secondary">
                          {planPriceLabel}
                        </Typography>
                      )}
                      {planPriceLabel && renewalLabel && (
                        <Typography variant="body2" color="text.secondary">
                          ·
                        </Typography>
                      )}
                      {renewalLabel && (
                        <Typography variant="body2" color="text.secondary">
                          {renewalLabel}
                        </Typography>
                      )}
                    </Stack>
                  )
                ) : (
                  <Skeleton variant="text" width={200} height={20} />
                )}
              </Stack>
              <Accordion
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: 'transparent',
                  backgroundImage: 'none',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ChevronDownIcon strokeWidth={1} />}
                  aria-controls="plan-details"
                  id="plan-details"
                  sx={{
                    flexDirection: 'row-reverse',
                    backgroundColor: 'transparent',
                    borderBottom: 'none !important',
                    pl: '0px !important',
                    pr: '8px !important',
                    minHeight: '16px !important',
                    maxHeight: '16px !important',
                  }}
                >
                  <Typography variant="body3" color="text.secondary" sx={{ textDecoration: 'underline' }}>
                    More details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'transparent', py: 2, pt: 6 }}>
                  {currentPlan ? (
                    planHighlights.length > 0 ? (
                      <Stack spacing={6}>
                        {/* Lead reassurance row, exactly as the matrix leads with
                            it: the whole product ships on every plan. */}
                        <PlanDetailRow
                          label={CORE_FEATURES_ROW.label}
                          tooltip={CORE_FEATURES_ROW.tooltip}
                          value
                          emphasized
                        />
                        {planHighlights.map((section) => (
                          <Stack key={section.title} spacing={4}>
                            <Typography
                              variant="body4"
                              sx={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                fontWeight: 600,
                                color: 'text.secondary',
                              }}
                            >
                              {section.title}
                            </Typography>
                            {section.items.map((item) => (
                              <PlanDetailRow
                                key={`${section.title}-${item.label}`}
                                label={item.label}
                                term={item.term}
                                tooltip={item.tooltip}
                                value={item.value}
                              />
                            ))}
                          </Stack>
                        ))}
                      </Stack>
                    ) : planFeatures.length > 0 ? (
                      <Stack spacing={4}>
                        {planFeatures.map((feature, index) => (
                          <Stack key={`${feature}-${index}`} direction="row" alignItems="center" spacing={2}>
                            {/* Neutral grey check — parity with the change-plan cards. */}
                            <CircleCheckBigIcon size={18} strokeWidth={1.5} color={theme.palette.grey[500]} />
                            <Typography variant="body3">{feature}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body3" color="text.secondary">
                        No plan features available.
                      </Typography>
                    )
                  ) : (
                    <Stack spacing={2}>
                      {[0, 1, 2].map((index) => (
                        <Skeleton key={index} variant="rounded" height={20} sx={{ width: '70%', borderRadius: 1 }} />
                      ))}
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Card>
        </Stack>


      </Stack>
    </Stack>
  )
}
